import { useEffect, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useAdminPrizeMessages, useResetPrizeMessages, useSavePrizeMessages,
} from '../../hooks/useQuestAdmin';
import { fmtDateTime } from './questShared';
import {
  BTN, BTN_SECONDARY, Card, ErrorNote, INPUT, LABEL, Loading,
} from './adminUi';

// The message on each prize's achievement card in the FSM app (the card a
// winner can save or post on LinkedIn): a short line, with the Nano value it
// speaks to shown under it. Each prize has a few; every prize won is dealt one
// and keeps it. The tech server holds the defaults and checks the limits
// (questSettingsService.js); these are the same limits, shown early.

const PRIZES = [
  ['quest', 'Points Champion', 'Most points in the quarter'],
  ['values', 'Values Ambassador', 'Most values recognitions in the quarter'],
  ['knowledge', 'Knowledge Champion', 'Best average quiz score in the quarter'],
  ['culture', 'Culture Champion', 'Most colleague recognitions in the quarter'],
  ['other', 'One-off prizes', 'Any other prize Comms give, such as a Safety Star'],
];
const MESSAGE_MAX = 160;
const MESSAGES_MAX = 8;

function PrizeMessages({
  award, title, how, data, values,
}) {
  const save = useSavePrizeMessages();
  const reset = useResetPrizeMessages();
  const [messages, setMessages] = useState(data.messages);
  // take in what the server has whenever it changes (after a save or a reset)
  useEffect(() => { setMessages(data.messages); }, [data.messages]);

  const changed = JSON.stringify(messages) !== JSON.stringify(data.messages);
  const setMessage = (i, patch) => setMessages((m) => m.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const tooLong = messages.some((m) => m.text.length > MESSAGE_MAX);
  const noValue = messages.some((m) => m.text.trim() && !values.includes(m.value));
  const empty = !messages.some((m) => m.text.trim());

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{how}</p>
        </div>
        <p className="text-[11px] text-gray-400">
          {data.customised
            ? `Edited${data.updated_by_name ? ` by ${data.updated_by_name}` : ''}${data.updated_at ? ` on ${fmtDateTime(data.updated_at)}` : ''}`
            : 'Default wording'}
        </p>
      </div>

      <div className="space-y-4 p-4">
        <ol className="space-y-4">
          {messages.map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className="flex gap-2">
              <span className="mt-2 w-5 shrink-0 text-right text-xs font-semibold text-gray-400">{i + 1}.</span>
              <div className="min-w-0 flex-1 space-y-2">
                <div className="max-w-xs">
                  <label className={LABEL} htmlFor={`${award}-${i}-value`}>Nano value</label>
                  <select id={`${award}-${i}-value`} value={m.value} onChange={(e) => setMessage(i, { value: e.target.value })}
                    className={`${INPUT} ${m.text.trim() && !values.includes(m.value) ? 'border-rose-400' : ''}`}>
                    {!values.includes(m.value) ? <option value="">Pick a value</option> : null}
                    {values.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL} htmlFor={`${award}-${i}-text`}>Message</label>
                  <textarea id={`${award}-${i}-text`} value={m.text} onChange={(e) => setMessage(i, { text: e.target.value })} rows={2}
                    className={`${INPUT} resize-y ${m.text.length > MESSAGE_MAX ? 'border-rose-400' : ''}`} />
                  <div className="mt-1 flex flex-wrap justify-between gap-2 text-[11px]">
                    <span className="italic text-gray-500">
                      {m.text.trim() ? `“${m.text.trim()}”${m.value ? ` — ${m.value}` : ''}` : 'Empty — left out when saved'}
                    </span>
                    <span className={m.text.length > MESSAGE_MAX ? 'text-rose-600' : 'text-gray-400'}>{m.text.length}/{MESSAGE_MAX}</span>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => setMessages((x) => x.filter((_, j) => j !== i))}
                disabled={messages.length <= 1} title="Remove this message"
                className="mt-6 h-8 w-8 shrink-0 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30">
                <XMarkIcon className="mx-auto h-4 w-4" />
              </button>
            </li>
          ))}
        </ol>
        {messages.length < MESSAGES_MAX ? (
          <button type="button" onClick={() => setMessages((x) => [...x, { value: values[0], text: '' }])}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline">
            <PlusIcon className="h-4 w-4" /> Add a message
          </button>
        ) : null}

        {save.isError ? <ErrorNote error={save.error} /> : null}
        {reset.isError ? <ErrorNote error={reset.error} fallback="That did not reset." /> : null}

        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!changed || tooLong || noValue || empty || save.isPending}
            onClick={() => save.mutate({ key: award, messages })} className={BTN}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {changed ? (
            <button type="button" onClick={() => setMessages(data.messages)} className={BTN_SECONDARY}>
              Undo changes
            </button>
          ) : null}
          {data.customised ? (
            <button type="button" disabled={reset.isPending}
              onClick={() => window.confirm(`Put ${title} back to the default wording?`) && reset.mutate({ key: award })}
              className={BTN_SECONDARY}>
              Reset to default wording
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function PrizeMessagesTab() {
  const q = useAdminPrizeMessages();
  return (
    <div className="space-y-4">
      <Card className="px-4 py-3 text-sm text-gray-600">
        <p>
          Every prize can be made into an achievement card in the FSM app, for the winner to save or post on LinkedIn.
          Under the prize&apos;s name, the card carries a short message and the Nano value it speaks to.
        </p>
        <p className="mt-2">
          Give each prize a few messages. Every prize won is dealt one, and it stays with that prize. People post their
          own cards, so write them without “you” or “they”, and keep them short — up to {MESSAGE_MAX} characters fits
          the card. Changes reach the app straight away.
        </p>
      </Card>
      <Loading q={q} what="the prize messages" />
      {q.data ? PRIZES.map(([key, title, how]) => (q.data.prizes?.[key] ? (
        <PrizeMessages key={key} award={key} title={title} how={how} data={q.data.prizes[key]} values={q.data.values || []} />
      ) : null)) : null}
    </div>
  );
}

export default PrizeMessagesTab;
